<template>
  <div class="sp-stepper">
    <sp-stepper-header>
      <div slot="preamble">
        <slot name="preamble" />
      </div>

      <sp-stepper-item
        v-for="(item, index) in items"
        :key="getItemValue(item)"
        :is-first="index === 0"
        :is-last="index === items.length - 1"
        :active="isActiveIndex(index)"
        :completed="isItemCompleted(item)"
        :title="getItemTitle(item)"
      ></sp-stepper-item>
    </sp-stepper-header>

    <sp-stepper-window :scrollable="scrollable">
      <div v-for="(_, index) in items" v-show="isActiveIndex(index)" :key="index">
        <slot :name="`item-${index + 1}`" />
      </div>
    </sp-stepper-window>

    <div class="sp-stepper__actions">
      <slot name="actions">
        <sp-stepper-actions
          v-if="!noActions"
          :disabled="disabledActions"
          @prev="prev"
          @next="next"
        ></sp-stepper-actions>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { toBoolean } from "#ce/utils/props";
import { props as stepperProps } from "./props.js";

const emit = defineEmits(["update-selected"]);

const props = defineProps(stepperProps);

const { exposeMethods } = useExpose();

exposeMethods({
  prev,
  next,
});

const currentIndex = ref(props.selected);

watch(
  () => props.selected,
  (index) => (currentIndex.value = index),
);

watch(currentIndex, (index) => emit("update-selected", index));

watch(() => props.items, resetCurrentIndex);

function resetCurrentIndex() {
  currentIndex.value = 0;
}

const noActions = computed(() => toBoolean(props.noActions));

function isActiveIndex(index) {
  return currentIndex.value === index;
}

function isItemCompleted(item) {
  return props.completed?.[getItemValue(item)] ?? getItemProps(item)?.completed ?? false;
}

function getItemTitle(item) {
  return item[props.itemTitle] ?? item;
}

function getItemValue(item) {
  return item[props.itemValue] ?? item;
}

function getItemProps(item) {
  return item[props.itemProps] ?? item;
}

const hasPrev = computed(() => currentIndex.value > 0);
const hasNext = computed(() => currentIndex.value < props.items.length - 1);

const disabledActions = computed(() => {
  if (!hasPrev.value && !hasNext.value) {
    return true;
  }

  if (!hasPrev.value) {
    return "prev";
  }

  if (!hasNext.value) {
    return "next";
  }

  return false;
});

function prev() {
  currentIndex.value = Math.max(0, currentIndex.value - 1);
}

function next() {
  currentIndex.value = Math.min(props.items.length - 1, currentIndex.value + 1);
}
</script>

<style>
:host {
  display: block;
}

:host([scrollable]:not([scrollable="false"])) {
  height: 100%;
}
</style>

<style scoped lang="scss">
.sp-stepper {
  width: 100%;
  position: relative;
  max-height: var(--sp-comp-stepper-max-height, 90vh);
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: minmax(0, 1fr);
  height: 100%;
}
</style>
